import Styles from './GoalSelection.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { MessageChat01, Marker2, Bag04 } from 'blaze-ui/icons/20'
import { Text } from 'blaze-ui/Text/Text'
import { useState } from 'react'
import { Pressable } from '@react-aria/interactions'
import cx from 'classnames'
import { Pill } from 'components/Pill/Pill'
import type { BusinessType, Goal } from 'types'
import { BUSINESS_TYPE_LABELS, GOAL_OPTIONS_BY_BUSINESS_TYPE } from './utils'
import { Heading } from 'blaze-ui/Heading/Heading'
import { strategiesDefaultData, Strategy } from '../StrategiesSelection/utils'
import { TopBar } from '../TopBar/TopBar'
import { Button } from 'blaze-ui/Button'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'

const BUSINESS_TYPE_OPTIONS: Array<{
  id: BusinessType
  title: string
  description: string
  icon: React.ComponentType<{ color?: string; size?: number }>
  iconColor: string
  backgroundColor: string
}> = [
  {
    id: 'service',
    title: 'Services',
    description: 'Agencies, consultants, freelancers',
    icon: MessageChat01,
    iconColor: 'var(--green)',
    backgroundColor: 'var(--green-10)',
  },
  {
    id: 'place',
    title: 'Local Businesses',
    description: 'Restaurants, stores, salons',
    icon: Marker2,
    iconColor: 'var(--starter)',
    backgroundColor: 'var(--starter-10)',
  },
  {
    id: 'product',
    title: 'Products',
    description: 'E-commerce, manufacturers',
    icon: Bag04,
    iconColor: 'var(--upgrade)',
    backgroundColor: 'var(--upgrade-10)',
  },
]

export interface GoalSelectionProps {
  onNext: (selectedBusinessType: BusinessType, goal: Goal, brandPlanStrategies: Strategy[]) => void
  onBack: () => void
  initialSelectedBusinessType?: BusinessType
  initialSelectedGoal?: Goal
}

export const GoalSelection = ({
  onNext,
  onBack,
  initialSelectedBusinessType = 'service',
  initialSelectedGoal,
}: GoalSelectionProps) => {
  const [selectedBusinessType, setSelectedBusinessType] = useState<BusinessType | undefined>(
    initialSelectedBusinessType,
  )
  const [selectedGoal, setSelectedGoal] = useState<Goal | undefined>(initialSelectedGoal)

  const goalOptions = selectedBusinessType ? GOAL_OPTIONS_BY_BUSINESS_TYPE[selectedBusinessType] : []
  const businessTypeLabel = selectedBusinessType ? BUSINESS_TYPE_LABELS[selectedBusinessType] : ''

  const handleBusinessTypeSelect = (businessType: BusinessType) => {
    setSelectedBusinessType(businessType)
    setSelectedGoal(undefined)
  }

  const handleNext = () => {
    if (selectedBusinessType && selectedGoal) {
      onNext(selectedBusinessType, selectedGoal, strategiesDefaultData[selectedBusinessType].strategies)
    }
  }

  const selectedBusinessTypeOption = BUSINESS_TYPE_OPTIONS.find((option) => option.id === selectedBusinessType)
  const isNextDisabled = !selectedBusinessType || !selectedGoal

  return (
    <MultiStepStepLayout title="What's your content goal?" scrollContainerClassName={Styles.scrollContainer}>
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <Button variant="tertiary" size="lg" onPress={onBack}>
                Back
              </Button>
              <Button size="lg" onPress={handleNext} isDisabled={isNextDisabled}>
                Next
              </Button>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <div className={Styles.sectionsContainer}>
          <TopBar highlightStep="goalsAndPlan" />
          <div className={Styles.section} role="group" aria-label="Business type selection">
            <Heading level={4}>Select your type of business</Heading>
            <div className={Styles.cardsContainer}>
              {BUSINESS_TYPE_OPTIONS.map((option) => {
                const Icon = option.icon
                const isSelected = selectedBusinessType === option.id

                return (
                  <Pressable key={option.id} onPress={() => handleBusinessTypeSelect(option.id)}>
                    <div
                      className={cx(Styles.card, { [Styles.cardSelected]: isSelected })}
                      data-testid={`goal-selection-card-${option.id}`}
                    >
                      <div className={Styles.iconWrapper} style={{ backgroundColor: option.backgroundColor }}>
                        <Icon size={28} color={option.iconColor} />
                      </div>
                      <div className={Styles.cardContent}>
                        <Heading level={4}>{option.title}</Heading>
                        <Text variant="secondary" color="var(--dark-60)">
                          {option.description}
                        </Text>
                      </div>
                    </div>
                  </Pressable>
                )
              })}
            </div>
          </div>

          {selectedBusinessType && goalOptions.length > 0 && (
            <div className={Styles.section} role="group" aria-label="Goal selection">
              <Heading level={4}>
                Choose the goal for your content plan based on what's best for your {businessTypeLabel} business
              </Heading>
              <div className={Styles.goalsContainer}>
                {goalOptions.map((goal) => {
                  const isSelected = selectedGoal === goal.id

                  return (
                    <Pressable key={goal.id} onPress={() => setSelectedGoal(goal.id)}>
                      <div
                        className={cx(Styles.goalCard, { [Styles.goalCardSelected]: isSelected })}
                        data-testid={`goal-option-${goal.id}`}
                      >
                        <div className={Styles.goalCardHeader}>
                          <Text variant="secondary" color="var(--dark-60)" className={Styles.goalCategory}>
                            {goal.category}
                          </Text>
                          {goal.isRecommended && (
                            <Pill
                              size="sm"
                              className={Styles.recommendedPill}
                              style={{
                                backgroundColor: selectedBusinessTypeOption?.backgroundColor,
                                color: selectedBusinessTypeOption?.iconColor,
                              }}
                            >
                              Recommended
                            </Pill>
                          )}
                        </div>
                        <Text variant="primary" color="var(--dark-90)" className={Styles.goalTitle}>
                          {goal.title}
                        </Text>
                        <Text variant="secondary" color="var(--dark-60)" className={Styles.goalDescription}>
                          {goal.description}
                        </Text>
                      </div>
                    </Pressable>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </StickyFooterProvider>
    </MultiStepStepLayout>
  )
}
